import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "using-packages-with-adobe-io-runtime"
    }}>{`Using Packages with Adobe I/O Runtime`}</h1>
    <p>{`Individual actions (functions) can be effective, but often you`}{`’`}{`ll need to create a set of actions that interact with each other; and you may wish to make those available to other developers as well. The means to do this in Runtime are called `}<em parentName="p">{`packages.`}</em></p>
    <p>{`Packages bundle related actions together. More than that, packages can contain both actions and `}<em parentName="p">{`feeds:`}</em>{` streams of events. Feeds are created by `}<em parentName="p">{`feed actions`}</em>{` that specify the trigger to fire for a given event source; each time the event is received, the selected trigger is fired and the associated action is executed. For more information, see `}<a parentName="p" {...{
        "href": "../reference/feeds.md",
        "title": "Using Feeds"
      }}>{`Using Feeds`}</a>{`.`}</p>
    <p>{`Everything you access in Runtime exists within a namespace. Your namespace is given to you when you sign up for Runtime access (see `}<a parentName="p" {...{
        "href": "../gettingstarted.md",
        "title": "Getting started"
      }}>{`Getting Started with Adobe I/O Runtime`}</a>{`). So, actions you create in Runtime, and packages as well, are stored in your namespace. Other users of Runtime get their own namespaces, and `}<strong parentName="p">{`Runtime includes a system namespace in which certain packages are stored available to all users.`}</strong></p>
    <h2 {...{
      "id": "creating-packages"
    }}>{`Creating packages`}</h2>
    <p>{`Packages not only bundle actions and feeds; they provide the means to share a common set of parameters across all entities in the package. Creating a package is simple. You can create a package in the Runtime CLI in one step:`}</p>
    <p><inlineCode parentName="p">{`aio rt:package:create hellopackage`}</inlineCode></p>
    <p>{`This creates a package in your namespace. You`}{`’`}{`ll get the following acknowledgement:`}</p>
    <pre><code parentName="pre" {...{}}>{`ok: created package hellopackage
`}</code></pre>
    <p>{`You can now get a summary of the package:`}</p>
    <p><inlineCode parentName="p">{`aio rt:package:get hellopackage`}</inlineCode></p>
    <p>{`This produces the following result:`}</p>
    <pre><code parentName="pre" {...{}}>{`package /<yourNamespace>/hellopackage
   (parameters: none defined)
`}</code></pre>
    <h2 {...{
      "id": "adding-actions-to-a-package"
    }}>{`Adding actions to a package`}</h2>
    <p>{`The package you created is empty; you haven`}{`’`}{`t added any actions yet. To add an action to the package, use the source code from the `}<inlineCode parentName="p">{`hello`}</inlineCode>{` action created in `}<a parentName="p" {...{
        "href": "../gettingstarted/deploy.md",
        "title": "Deploying your first function"
      }}>{`Deploying your first Adobe I/O Runtime function`}</a>{`. Here is the code for that action:`}</p>
    <pre><code parentName="pre" {...{}}>{`function main(params) {
  return { payload: 'Hello ' + params.name };
}
`}</code></pre>
    <p>{`Add this action to the package with the following command:`}</p>
    <p><inlineCode parentName="p">{`aio rt:action:create hellopackage/hello hello.js`}</inlineCode></p>
    <p>{`Here`}{`’`}{`s the response from the CLI:`}</p>
    <pre><code parentName="pre" {...{}}>{`ok: created action hellopackage/hello
`}</code></pre>
    <p>{`This is just like creating an action by itself; you merely need to preface the action name with the name of the package you want to put it in. Note, however, that this isn`}{`’`}{`t the same action as the one you created from this code earlier; that earlier action exists in your namespace outside any package. You`}{`’`}{`ve just created a new action and stored it in the package. There`}{`’`}{`s no way to move an existing action into a package, or move an action from one package to another.`}</p>
    <p>{`Now, if you get a summary again, you`}{`’`}{`ll see the following:`}</p>
    <pre><code parentName="pre" {...{}}>{`package /<yourNamespace>/hellopackage
   (parameters: none defined)
 action /<yourNamespace>/hellopackage/hello
   (parameters: none defined)
`}</code></pre>
    <InlineAlert slots="text" mdxType="InlineAlert" />
    <p>{`You can`}{`’`}{`t add a package to a package; in other words, you can`}{`’`}{`t nest packages.`}</p>
    <h2 {...{
      "id": "invoking-actions-in-a-package"
    }}>{`Invoking actions in a package`}</h2>
    <p>{`Invoking an action is also straightforward; you just need to preface the action`}{`’`}{`s name with its package.`}</p>
    <p><inlineCode parentName="p">{`aio rt:action:invoke --result hellopackage/hello --param name <your name>`}</inlineCode></p>
    <p>{`You should get this output:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "payload": "Hello <your name>"
}
`}</code></pre>
    <h2 {...{
      "id": "adding-parameters-to-a-package"
    }}>{`Adding parameters to a package`}</h2>
    <p>{`Notice in the package summary the statements `}<inlineCode parentName="p">{`(parameters: none defined)`}</inlineCode>{`. You can add default parameters to a package, and all entities in the package will inherit them:`}</p>
    <p><inlineCode parentName="p">{`aio rt:package:update hellopackage --param name Patricia`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`ok: updated package hellopackage
`}</code></pre>
    <h3 {...{
      "id": "default-params-and-encryption"
    }}>{`Default params and encryption`}</h3>
    <p>{`Before we dive deeper in how to set and use default params, let’s discuss the security aspect first. Many developers use the default params as a mechanism to provision actions with the secrets/passwords needed to authenticate against some other systems (databases, services, APIs).`}</p>
    <p>{`In order to support this use case, all default params are automatically encrypted. They are decrypted just before the action code is executed. Thus, the only time you have access to the decrypted value is while executing the action code.`}</p>
    <p>{`If you run the CLI command for getting an action or package, you’d get a listing for the names of the default params while the values will be listed as a hash instead of the actual value.`}</p>
    <p>{`You can see what parameters have been added to a package (note the `}<inlineCode parentName="p">{`summary`}</inlineCode>{` flag is left out; you`}{`’`}{`ll get a complete report):`}</p>
    <p><inlineCode parentName="p">{`aio rt:package:get hellopackage`}</inlineCode></p>
    <p>{`In the response, you`}{`’`}{`ll see:`}</p>
    <pre><code parentName="pre" {...{}}>{`ok: got package hellopackage
...
"parameters": [
    {
        "key": "name",
        "value": some_hash
    }
]
...
`}</code></pre>
    <p>{`You can also verify that your actions inherit the parameters you set for the package:`}</p>
    <p><inlineCode parentName="p">{`aio rt:action:get hellopackage/hello`}</inlineCode></p>
    <p>{`You`}{`’`}{`ll see in the response:`}</p>
    <pre><code parentName="pre" {...{}}>{`ok: got action hello
...
"parameters": [
    {
        "key": "name",
        "value": some_hash
    }
]
...
`}</code></pre>
    <p>{`If you get the package summary again, you`}{`’`}{`ll see the default parameters listed:`}</p>
    <pre><code parentName="pre" {...{}}>{`package /<yourNamespace>/hellopackage: Returns a result based on parameter name
   (parameters: *name)
 action /<yourNamespace>/hellopackage/hello
   (parameters: none defined)
`}</code></pre>
    <p>{`The asterisk next to `}{`“`}{`name`}{`”`}{` in the parameters list for the package indicates that it`}{`’`}{`s `}<em parentName="p">{`bound`}</em>{` (see `}{`“`}{`Package bindings`}{`”`}{` below). A single asterisk denotes a parameter has a default value defined. A double asterisk denotes a parameter with a `}<em parentName="p">{`finalized`}</em>{` default value: such values cannot be changed by the user.`}</p>
    <p>{`Now, if you invoke the action without specifying any parameters, you can see that it inherits from the package`}{`’`}{`s parameters:`}</p>
    <p><inlineCode parentName="p">{`aio rt:action:invoke --result hellopackage/hello`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "payload": "Hello Patricia"
}
`}</code></pre>
    <p>{`Of course, you can also override those parameters by supplying your own values:`}</p>
    <p><inlineCode parentName="p">{`aio rt:action:invoke --result hellopackage/hello --param name Jennifer`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "payload": "Hello Jennifer"
}
`}</code></pre>
    <p>{`You can also override a package`}{`’`}{`s default parameters for a given action by setting default parameters specifically for that action:`}</p>
    <p><inlineCode parentName="p">{`aio rt:action:update hellopackage/hello --param name Christine`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`ok: updated action hellopackage/hello
`}</code></pre>
    <h2 {...{
      "id": "package-bindings"
    }}>{`Package bindings`}</h2>
    <p>{`Depending on how you use your package, you may find you need to invoke an action with default parameters, without, or with a different set of default parameters. In such cases, rather than create a set of default parameters directly on the package, you can create a `}<em parentName="p">{`package binding:`}</em>{` a named set of default parameters associated with a package.`}</p>
    <p><inlineCode parentName="p">{`aio rt:package:bind hellopackage helloMyName --param name`}</inlineCode>{` `}<em parentName="p"><inlineCode parentName="em">{`<your name>`}</inlineCode></em></p>
    <pre><code parentName="pre" {...{}}>{`ok: created binding helloMyName
`}</code></pre>
    <p>{`The package binding is a shortcut for `}{`“`}{`Call this package with these parameters.`}{`”`}{` Unlike setting default parameters on the package, this leaves the package itself (and its entities) open to be called without the parameters defined in the binding. It also leaves open the option to create another binding with another set of defaults. There`}{`’`}{`s no limit to the bindings you can create for a given package.`}</p>
    <InlineAlert slots="text" mdxType="InlineAlert" />
    <p>{`Package bindings are very useful for another reason: there`}{`’`}{`s no way to remove default parameters from a package or action once you`}{`’`}{`ve set them directly. Using bindings is the only way to preserve the option to call an action in the package without the defaults.`}</p>
    <p>{`Each package binding is used as if it were a package itself. To call an action in the package associated with a given binding, use the name of the binding in your invocation instead of the name of the package:`}</p>
    <p><inlineCode parentName="p">{`aio rt:action:invoke --result helloMyName/hello`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`{
  "payload": "Hello Christine"
}
`}</code></pre>
    <InlineAlert slots="text" mdxType="InlineAlert" />
    <p>{`The output above reads `}<inlineCode parentName="p">{`Hello Christine`}</inlineCode>{` because we've defined a default value at the action level. This takes precedence over values set at the package level or invocation time. When you share a package, this ensures that your params can't be overwritten at execution time. However if you intend to let the user define its own values, then you shouldn't define a default value.`}</p>
    <p>{`You can also substitute the name of the binding for the package name in other package commands:`}</p>
    <p><inlineCode parentName="p">{`aio rt:package:get helloMyName`}</inlineCode></p>
    <h2 {...{
      "id": "browsing-packages"
    }}>{`Browsing packages`}</h2>
    <p>{`Over time, you`}{`’`}{`ll develop many packages, and you may want to see them all. Of course there`}{`’`}{`s a CLI command for this:`}</p>
    <p><inlineCode parentName="p">{`aio rt:package:list`}</inlineCode></p>
    <p>{`This lists all the packages in the given namespace. Run it on yours now, and you should see both your package and the binding you created:`}</p>
    <pre><code parentName="pre" {...{}}>{`packages
/<yourNamespace>/hellopackage                                            private
/<yourNamespace>/helloMyName                                             private
`}</code></pre>
    <h2 {...{
      "id": "sharing-packages"
    }}>{`Sharing packages`}</h2>
    <p>{`Once you`}{`’`}{`re sure your package is ready for others to use, you can share it. Notice that the packages listed when you browsed your namespace are shown as `}<inlineCode parentName="p">{`private`}</inlineCode>{`; only you can see them. To share a package:`}</p>
    <p><inlineCode parentName="p">{`aio rt:package:update hellopackage --shared yes`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`ok: updated package hellopackage
`}</code></pre>
    <p>{`You can easily verify your package is shared:`}</p>
    <p><inlineCode parentName="p">{`aio rt:package:get hellopackage`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`ok: got package hellopackage
...
"publish": true
...
`}</code></pre>
    <p>{`If you list your packages again, the package you shared will be listed as such:`}</p>
    <pre><code parentName="pre" {...{}}>{`packages
/<yourNamespace>/hellopackage                                            shared
/<yourNamespace>/helloMyName                                             private
`}</code></pre>
    <h3 {...{
      "id": "un-sharing-packages"
    }}>{`Un-sharing packages`}</h3>
    <p>{`If you wish to make your package private again, you can update the package at any time. To un-share a package: `}</p>
    <p><inlineCode parentName="p">{`aio rt:package:update hellopackage --shared no`}</inlineCode>{` `}</p>
    <p>{`You can easily verify your package is private:`}</p>
    <p><inlineCode parentName="p">{`aio rt:package:get hellopackage`}</inlineCode></p>
    <pre><code parentName="pre" {...{}}>{`...
"publish": false
...
`}</code></pre>
    <p>{`If you list your packages again, the package you made private will be listed as such:`}</p>
    <pre><code parentName="pre" {...{}}>{`packages
/<yourNamespace>/hellopackage                                            private
/<yourNamespace>/helloMyName                                             private
`}</code></pre>
    <p>{`Others can now use your package just as you can, so long as you give them the fully qualified name of the package, including your namespace.`}</p>
    <h2 {...{
      "id": "shared-packages-and-permissions"
    }}>{`Shared Packages and Permissions`}</h2>
    <p>{`Shared packages enforce `}<inlineCode parentName="p">{`execute-only`}</inlineCode>{` permission for any operation that is initiated from outside the namespace owning the package. Assuming there is a package `}<inlineCode parentName="p">{`my-package`}</inlineCode>{` in namespace `}<inlineCode parentName="p">{`a`}</inlineCode>{`, this package is shared, and there is a namespace `}<inlineCode parentName="p">{`b`}</inlineCode>{` who uses the shared package, then:`}</p>
    <ul>
      <li parentName="ul">{`Invoking `}<inlineCode parentName="li">{`my-package`}</inlineCode>{` actions from namespace `}<inlineCode parentName="li">{`b`}</inlineCode>{` or `}<inlineCode parentName="li">{`a`}</inlineCode>{` will work`}</li>
      <li parentName="ul">{`Trying to get `}<inlineCode parentName="li">{`my-package`}</inlineCode>{` code or edit it (update/delete) from namespace `}<inlineCode parentName="li">{`b`}</inlineCode>{` will fail`}</li>
      <li parentName="ul">{`Manage `}<inlineCode parentName="li">{`my-package`}</inlineCode>{` (create/read/update/delete) will only work from namespace `}<inlineCode parentName="li">{`a`}</inlineCode></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      